import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { fetchAllPaginated } from '../utils/brewfatherSync';
import { hasAppVersionChanged, updateCachedAppVersion } from '../utils/versionMarker';
import { APP_VERSION } from '../index';
import logger from '../utils/logger';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useOptionalBrewery } from './BreweryContext';
// Lightweight localStorage cache for inventory, mirroring Batch cache behavior
const INV_CACHE_KEYS = {
  fermentables: 'brewfather_inventory_fermentables',
  hops: 'brewfather_inventory_hops',
  yeasts: 'brewfather_inventory_yeasts',
  miscs: 'brewfather_inventory_miscs',
  lastRefresh: 'brewfather_inventory_last_refresh'
};
const INV_REFRESH_DELAY_MS = 15 * 60 * 1000;
const INV_SCHEMA_VERSION = 2;
const INV_SCHEMA_VERSION_KEY = 'brewfather_inventory_schema_version';

const getCachedInventory = (type: keyof Omit<typeof INV_CACHE_KEYS, 'lastRefresh'>): InventoryItem[] | null => {
  try {
    const raw = localStorage.getItem(INV_CACHE_KEYS[type]);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const setCachedInventory = (type: keyof Omit<typeof INV_CACHE_KEYS, 'lastRefresh'>, items: InventoryItem[]): void => {
  try {
    logger.log(`setCachedInventory - Storing ${items.length} items for ${type}`);
    localStorage.setItem(INV_CACHE_KEYS[type], JSON.stringify(items));
    localStorage.setItem(INV_CACHE_KEYS.lastRefresh, Date.now().toString());
  } catch (e) {
    logger.error(`setCachedInventory - Error storing ${type}:`, e);
    // Best-effort caching; if it fails, app continues with fresh data from API
  }
};

const getInventoryLastRefresh = (): number | null => {
  const v = localStorage.getItem(INV_CACHE_KEYS.lastRefresh);
  return v ? parseInt(v, 10) : null;
};

const shouldRefreshInventory = (): boolean => {
  const last = getInventoryLastRefresh();
  if (!last) return true;
  return Date.now() - last > INV_REFRESH_DELAY_MS;
};

// Export clearInventoryCache for external use (e.g., Settings component)
export const clearInventoryCache = (): void => {
  logger.log("[InventoryCache] Clearing all inventory cache");
  localStorage.removeItem(INV_CACHE_KEYS.fermentables);
  localStorage.removeItem(INV_CACHE_KEYS.hops);
  localStorage.removeItem(INV_CACHE_KEYS.yeasts);
  localStorage.removeItem(INV_CACHE_KEYS.miscs);
  localStorage.removeItem(INV_CACHE_KEYS.lastRefresh);
};

export interface InventoryItem {
  _id: string;
  name: string;
  inventory: number;
  supplier?: string; // For yeasts, this will store laboratory
  cost?: number;
  costPerAmount?: number;
  costPerAmountUnit?: string;
  type?: string;
  origin?: string;
  unit?: string;
  amount?: number;
  // Fermentable specific
  color?: number;
  potential?: number;
  moisture?: number;
  diastaticPower?: number;
  protein?: number;
  maxInBatch?: number;
  notes?: string;
  recommendedMash?: boolean;
  addAfterBoil?: boolean;
  coarseFineDiff?: number;
  yield?: number;
  ibuGalPerLb?: number;
  displayAmount?: string;
  displayColor?: string;
  displayCost?: string;
  // Hop specific
  alpha?: number;
  beta?: number;
  form?: string;
  // Yeast specific
  attenuation?: number;
  flocculation?: string;
  temperature?: string;
  tolerance?: number; // maps to maxAbv for yeasts if available
  fermentsAll?: boolean;
  maxAbv?: number;
  maxAttenuation?: number | null;
  minAttenuation?: number | null;
  minTemp?: number;
  maxTemp?: number;
  laboratory?: string;
  description?: string;
  productId?: string;
  manufacturingDate?: string | null;
  bestBeforeDate?: string | null;
  // Misc specific
  use?: string;
  time?: number;
  amountIsWeight?: boolean;
}

interface InventoryContextType {
  // Inventory data by type
  fermentables: InventoryItem[];
  hops: InventoryItem[];
  yeasts: InventoryItem[];
  miscs: InventoryItem[];

  // Loading states
  isLoading: boolean;
  isRefetching: boolean;

  // Error state
  error: string | null;

  // Actions
  refreshInventory: () => Promise<void>;
  refreshInventoryForTypes: (types: string[]) => Promise<void>;

  // Cache info
  lastSynced: Date | null;

  // Utility functions
  getInventoryById: (id: string, type: 'fermentables' | 'hops' | 'yeasts' | 'miscs') => InventoryItem | undefined;
  getInventoryByName: (name: string, type: 'fermentables' | 'hops' | 'yeasts' | 'miscs') => InventoryItem | undefined;
}

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

interface InventoryProviderProps {
  children: ReactNode;
  apiKey: string | null;
  breweryId?: string | null;
}

export const InventoryProvider: React.FC<InventoryProviderProps> = ({ children, apiKey, breweryId }) => {
  const breweryCtx = useOptionalBrewery();
  const [fermentables, setFermentables] = useState<InventoryItem[]>([]);
  const [hops, setHops] = useState<InventoryItem[]>([]);
  const [yeasts, setYeasts] = useState<InventoryItem[]>([]);
  const [miscs, setMiscs] = useState<InventoryItem[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);

  // Fetch inventory for specific types with pagination
  const fetchInventoryForTypes = useCallback(async (types: string[]) => {
    if (breweryId) return; // Brewery data is pushed by the server sync
    if (!apiKey) {
      logger.log("InventoryContext - No API key provided");
      return;
    }

    logger.log('InventoryContext - Fetching inventory for types:', types);
    try {
      const allData: { [key: string]: any[] } = {};
      const failedTypes: string[] = [];

      // Fetch each type with shared pagination + retry. A failed type keeps its
      // existing state and cache — partial data must never overwrite good data.
      for (const type of types) {
        try {
          allData[type] = await fetchAllPaginated({
            endpoint: `inventory/${type}`,
            apiKey,
            params: { complete: true },
          });
          logger.log(`InventoryContext - Total ${type} items fetched: ${allData[type].length}`);
        } catch (error) {
          logger.error(`InventoryContext - Error fetching ${type}:`, error);
          failedTypes.push(type);
        }
      }

      // Update state based on types requested and cache results
      types.filter((type) => !failedTypes.includes(type)).forEach((type) => {
        const data = allData[type].map((item: any) => {
          const mapped = {
          _id: item._id,
          name: item.name,
          inventory: item.inventory || 0,
          supplier: item.laboratory || item.supplier, // prefer laboratory for yeasts
          cost: item.cost,
          costPerAmount: item.costPerAmount,
          costPerAmountUnit: item.costPerAmountUnit,
          type: item.type,
          origin: item.origin,
          unit: item.unit,
          amount: item.amount,
          notes: item.userNotes ?? item.notes ?? item.description,
          // Fermentable specific
          color: item.color,
          potential: item.potential,
          moisture: item.moisture,
          diastaticPower: item.diastaticPower,
          protein: item.protein,
          maxInBatch: item.maxInBatch,
          
          recommendedMash: item.recommendedMash,
          addAfterBoil: item.addAfterBoil,
          coarseFineDiff: item.coarseFineDiff,
          yield: item.yield,
          ibuGalPerLb: item.ibuGalPerLb,
          displayAmount: item.displayAmount,
          displayColor: item.displayColor,
          displayCost: item.displayCost,
          // Hop specific
          alpha: item.alpha,
          beta: item.beta,
          form: item.form,
          // Yeast specific
          attenuation: item.attenuation,
          flocculation: item.flocculation,
          temperature: ((): string | undefined => {
            if (typeof item.temperature === 'string' && item.temperature) return item.temperature;
            // Build temperature string from minTemp/maxTemp if present
            if (item.minTemp !== undefined && item.maxTemp !== undefined) {
              // item.minTemp appears in Celsius in sample; format to 0-1 decimal
              const min = Number(item.minTemp);
              const max = Number(item.maxTemp);
              if (!isNaN(min) && !isNaN(max)) return `${min.toFixed(1)}-${max.toFixed(1)} °C`;
            }
            return undefined;
          })(),
          tolerance: item.tolerance ?? item.maxAbv,
          fermentsAll: item.fermentsAll,
          maxAbv: item.maxAbv,
          maxAttenuation: item.maxAttenuation ?? null,
          minAttenuation: item.minAttenuation ?? null,
          minTemp: item.minTemp,
          maxTemp: item.maxTemp,
          laboratory: item.laboratory,
          description: item.description,
          productId: item.productId,
          manufacturingDate: item.manufacturingDate ?? null,
          bestBeforeDate: item.bestBeforeDate ?? null,
          // Misc specific
          use: item.use,
          time: item.time,
          amountIsWeight: item.amountIsWeight,
        };

        return mapped;
      });

        switch (type) {
          case 'fermentables':
            setFermentables(data);
            setCachedInventory('fermentables', data);
            break;
          case 'hops':
            setHops(data);
            setCachedInventory('hops', data);
            break;
          case 'yeasts':
            setYeasts(data);
            setCachedInventory('yeasts', data);
            break;
          case 'miscs':
            setMiscs(data);
            setCachedInventory('miscs', data);
            break;
        }
      });

      if (failedTypes.length > 0) {
        setError(`Failed to refresh inventory: ${failedTypes.join(', ')}`);
        return;
      }

      setLastSynced(new Date());
      try {
        localStorage.setItem(INV_SCHEMA_VERSION_KEY, String(INV_SCHEMA_VERSION));
      } catch {}
      // Only a successful refresh of every type counts as a full sync for this app version
      if (['fermentables', 'hops', 'yeasts', 'miscs'].every((t) => types.includes(t))) {
        updateCachedAppVersion('inventory', APP_VERSION);
      }
      setError(null);
    } catch (err) {
      logger.error('Error fetching inventory:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch inventory');
    }
  }, [apiKey, breweryId]);

  // Brewery mode: refresh triggers the server-side brewerySync Cloud Function
  // (admin only; errors surface through syncError — see BatchContext)
  const brewerySyncRefresh = useCallback(async () => {
    if (!breweryCtx?.canSync) return;
    await breweryCtx.syncBreweryData().catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breweryCtx?.canSync, breweryCtx?.syncBreweryData]);

  // Background refresh — still sets the busy flag so headers spin, but
  // doesn't clear data on screen while it runs
  const refreshInventoryInBackground = useCallback(async () => {
    if (breweryId) return brewerySyncRefresh();
    try {
      setIsRefetching(true);
      await fetchInventoryForTypes(['fermentables', 'hops', 'yeasts', 'miscs']);
    } finally {
      setIsRefetching(false);
    }
  }, [breweryId, brewerySyncRefresh, fetchInventoryForTypes]);

  // Refresh all inventory
  const refreshInventory = useCallback(async () => {
    if (breweryId) return brewerySyncRefresh();
    try {
      setIsRefetching(true);
      await fetchInventoryForTypes(['fermentables', 'hops', 'yeasts', 'miscs']);
    } finally {
      setIsRefetching(false);
    }
  }, [breweryId, brewerySyncRefresh, fetchInventoryForTypes]);

  // Refresh inventory for specific types - background refresh that doesn't clear UI
  const refreshInventoryForTypes = useCallback(async (types: string[]) => {
    if (breweryId) return brewerySyncRefresh();
    try {
      setIsRefetching(true);
      await fetchInventoryForTypes(types);
    } finally {
      setIsRefetching(false);
    }
  }, [breweryId, brewerySyncRefresh, fetchInventoryForTypes]);

  // Utility functions
  const getInventoryById = useCallback((id: string, type: 'fermentables' | 'hops' | 'yeasts' | 'miscs') => {
    const items = type === 'fermentables' ? fermentables : 
                  type === 'hops' ? hops : 
                  type === 'yeasts' ? yeasts : miscs;
    return items.find(item => item._id === id);
  }, [fermentables, hops, yeasts, miscs]);

  const getInventoryByName = useCallback((name: string, type: 'fermentables' | 'hops' | 'yeasts' | 'miscs') => {
    const items = type === 'fermentables' ? fermentables : 
                  type === 'hops' ? hops : 
                  type === 'yeasts' ? yeasts : miscs;
    return items.find(item => item.name === name);
  }, [fermentables, hops, yeasts, miscs]);

  // Brewery mode: listen to Firestore for inventory data
  useEffect(() => {
    if (!breweryId) return;

    logger.log('InventoryContext: Brewery mode - listening to Firestore for inventory');
    setIsLoading(true);

    const types = ['fermentables', 'hops', 'yeasts', 'miscs'] as const;
    const unsubscribes: (() => void)[] = [];

    types.forEach((type) => {
      const inventoryRef = doc(db, 'breweries', breweryId, 'inventory', type);
      const unsub = onSnapshot(
        inventoryRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            const items: InventoryItem[] = (data.items || []).map((item: any) => ({
              _id: item._id,
              name: item.name,
              inventory: item.inventory || 0,
              supplier: item.laboratory || item.supplier,
              cost: item.cost,
              costPerAmount: item.costPerAmount,
              costPerAmountUnit: item.costPerAmountUnit,
              type: item.type,
              origin: item.origin,
              unit: item.unit,
              amount: item.amount,
              notes: item.userNotes ?? item.notes ?? item.description,
              color: item.color,
              potential: item.potential,
              moisture: item.moisture,
              diastaticPower: item.diastaticPower,
              protein: item.protein,
              maxInBatch: item.maxInBatch,
              recommendedMash: item.recommendedMash,
              addAfterBoil: item.addAfterBoil,
              coarseFineDiff: item.coarseFineDiff,
              yield: item.yield,
              ibuGalPerLb: item.ibuGalPerLb,
              displayAmount: item.displayAmount,
              displayColor: item.displayColor,
              displayCost: item.displayCost,
              alpha: item.alpha,
              beta: item.beta,
              form: item.form,
              attenuation: item.attenuation,
              flocculation: item.flocculation,
              temperature: (() => {
                if (typeof item.temperature === 'string' && item.temperature) return item.temperature;
                if (item.minTemp !== undefined && item.maxTemp !== undefined) {
                  const min = Number(item.minTemp);
                  const max = Number(item.maxTemp);
                  if (!isNaN(min) && !isNaN(max)) return `${min.toFixed(1)}-${max.toFixed(1)} °C`;
                }
                return undefined;
              })(),
              tolerance: item.tolerance ?? item.maxAbv,
              fermentsAll: item.fermentsAll,
              maxAbv: item.maxAbv,
              maxAttenuation: item.maxAttenuation ?? null,
              minAttenuation: item.minAttenuation ?? null,
              minTemp: item.minTemp,
              maxTemp: item.maxTemp,
              laboratory: item.laboratory,
              description: item.description,
              productId: item.productId,
              manufacturingDate: item.manufacturingDate ?? null,
              bestBeforeDate: item.bestBeforeDate ?? null,
              use: item.use,
              time: item.time,
              amountIsWeight: item.amountIsWeight,
            }));

            switch (type) {
              case 'fermentables': setFermentables(items); break;
              case 'hops': setHops(items); break;
              case 'yeasts': setYeasts(items); break;
              case 'miscs': setMiscs(items); break;
            }
          }
          setIsLoading(false);
          setLastSynced(new Date());
        },
        (error) => {
          logger.error(`InventoryContext: Error listening to Firestore ${type}:`, error);
          setIsLoading(false);
        }
      );
      unsubscribes.push(unsub);
    });

    return () => {
      unsubscribes.forEach((unsub) => unsub());
      // isLoading is shared between the brewery and client paths. If this
      // effect tears down before any snapshot arrived (e.g. the data-source
      // decision flips during startup), the flag would otherwise be stranded
      // true and every view of this domain spins forever.
      setIsLoading(false);
    };
  }, [breweryId]);

  // Load inventory when API key changes - homebrewer path
  useEffect(() => {
    if (breweryId) return; // Skip for brewery accounts
    if (apiKey) {
      // Try cache first to keep UI populated during refresh
      const cachedF = getCachedInventory('fermentables');
      const cachedH = getCachedInventory('hops');
      const cachedY = getCachedInventory('yeasts');
      const cachedM = getCachedInventory('miscs');
      const anyCache = cachedF || cachedH || cachedY || cachedM;

      if (cachedF) setFermentables(cachedF);
      if (cachedH) setHops(cachedH);
      if (cachedY) setYeasts(cachedY);
      if (cachedM) setMiscs(cachedM);
      const last = getInventoryLastRefresh();
      if (last) setLastSynced(new Date(last));

      // Detect outdated cache schema or yeast cache missing newly mapped fields; trigger targeted refresh
      const schemaVersion = Number(localStorage.getItem(INV_SCHEMA_VERSION_KEY) || '0');
      const yeastCacheMissingFields = Array.isArray(cachedY) && cachedY.length > 0 && cachedY.some((y: any) => (
        y.laboratory === undefined || y.maxAbv === undefined || y.unit === undefined || y.amount === undefined
      ));

      if (!anyCache) {
        logger.log('InventoryContext - No cache found, loading inventory...');
        setIsLoading(true);
        refreshInventory().finally(() => setIsLoading(false));
      } else if (hasAppVersionChanged('inventory', APP_VERSION)) {
        logger.log('InventoryContext - App version changed, forcing full background refresh');
        refreshInventoryInBackground();
      } else if (schemaVersion < INV_SCHEMA_VERSION || yeastCacheMissingFields) {
        logger.log('InventoryContext - Inventory schema upgrade or yeast cache missing fields, refreshing yeasts...');
        setIsRefetching(true);
        refreshInventoryForTypes(['yeasts']).finally(() => setIsRefetching(false));
      } else if (shouldRefreshInventory()) {
        // Background refresh only if cache is stale - don't set isRefetching to avoid UI flicker
        logger.log('InventoryContext - Cache is stale, triggering background refresh');
        refreshInventoryInBackground();
      }
    }
  }, [apiKey, breweryId, refreshInventory, refreshInventoryInBackground, refreshInventoryForTypes]);

  const value: InventoryContextType = useMemo(() => ({
    fermentables,
    hops,
    yeasts,
    miscs,
    isLoading,
    isRefetching: breweryId ? (breweryCtx?.isSyncing ?? false) : isRefetching,
    error: breweryId ? (error || breweryCtx?.syncError || null) : error,
    refreshInventory,
    refreshInventoryForTypes,
    lastSynced: breweryId ? (breweryCtx?.lastSyncedAt ?? lastSynced) : lastSynced,
    getInventoryById,
    getInventoryByName,
  }), [
    fermentables, hops, yeasts, miscs, isLoading, isRefetching, error,
    refreshInventory, refreshInventoryForTypes, lastSynced,
    breweryId, breweryCtx?.isSyncing, breweryCtx?.syncError, breweryCtx?.lastSyncedAt,
    getInventoryById, getInventoryByName,
  ]);

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};

export const useInventory = (): InventoryContextType => {
  const context = useContext(InventoryContext);
  if (context === undefined) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};
